import { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Search, Plus, Users, Filter, Loader2, ArrowUpRight, Star } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { contactsApi, usersApi, type ContactListParams, type ContactStatus } from "@/lib/api"
import { formatPhone } from "@shared/utils/phone"
import { formatCurrency } from "@shared/utils/currency"
import { timeAgo } from "@shared/utils/date"

const STATUS_OPTIONS: { value: ContactStatus | ""; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "active", label: "Active" },
  { value: "client", label: "Client" },
  { value: "inactive", label: "Inactive" },
]

const STATUS_VARIANTS: Record<ContactStatus, "info" | "warning" | "success" | "default" | "muted"> =
  {
    new: "info",
    contacted: "warning",
    active: "default",
    client: "success",
    inactive: "muted",
  }

export default function ContactsListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get("q") ?? "")

  const params: ContactListParams = {
    q: searchParams.get("q") ?? undefined,
    status: (searchParams.get("status") as ContactStatus) || undefined,
    ownerId: searchParams.get("ownerId") ?? undefined,
    source: searchParams.get("source") ?? undefined,
    page: Number(searchParams.get("page") ?? 1),
    pageSize: 50,
  }

  const contacts = useQuery({
    queryKey: ["contacts", params],
    queryFn: () => contactsApi.list(params),
  })

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  const sources = useQuery({
    queryKey: ["contacts-sources"],
    queryFn: () => contactsApi.sources(),
    // Sources change rarely; cache for 5 min.
    staleTime: 5 * 60 * 1000,
  })

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete("page")
    setSearchParams(next)
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateFilter("q", searchInput.trim())
  }

  const actions = (
    <Button asChild>
      <Link to="/contacts/new">
        <Plus className="h-4 w-4" />
        New Contact
      </Link>
    </Button>
  )

  return (
    <AppShell title="Contacts" subtitle="Manage your finance broker contacts" actions={actions}>
      <div className="space-y-4">
        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <form onSubmit={handleSearchSubmit} className="relative flex-1">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search by name, business, email, mobile, ABN…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </form>
            <div className="flex items-center gap-2">
              <Filter className="text-muted-foreground h-4 w-4" />
              <select
                className="border-input bg-background h-10 rounded-md border px-3 text-sm"
                value={params.status ?? ""}
                onChange={(e) => updateFilter("status", e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                className="border-input bg-background h-10 rounded-md border px-3 text-sm"
                value={params.ownerId ?? ""}
                onChange={(e) => updateFilter("ownerId", e.target.value)}
              >
                <option value="">All brokers</option>
                {brokers.data?.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName}
                  </option>
                ))}
              </select>
              <Input
                list="contact-sources"
                placeholder="Source…"
                value={params.source ?? ""}
                onChange={(e) => updateFilter("source", e.target.value)}
                className="h-10 w-40"
              />
              <datalist id="contact-sources">
                {sources.data?.sources.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>
          </div>
        </Card>

        {/* Results */}
        {contacts.isLoading ? (
          <Card className="grid place-items-center p-16">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </Card>
        ) : contacts.error ? (
          <Card className="text-destructive p-8 text-center">
            Error loading contacts: {(contacts.error as Error).message}
          </Card>
        ) : !contacts.data?.items.length ? (
          <Card className="p-16 text-center">
            <div className="bg-rc-blue-50 text-rc-blue-600 mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full">
              <Users className="h-7 w-7" />
            </div>
            <h3 className="text-foreground text-lg font-semibold">No contacts yet</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {params.q || params.status || params.ownerId || params.source
                ? "No contacts match the current filters."
                : "Get started by creating your first contact."}
            </p>
            <Button asChild className="mt-4">
              <Link to="/contacts/new">
                <Plus className="h-4 w-4" />
                New Contact
              </Link>
            </Button>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-border bg-rc-grey-50 text-muted-foreground border-b text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Name</th>
                  <th className="px-4 py-3 text-left font-medium">Business</th>
                  <th className="px-4 py-3 text-left font-medium">Contact</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Owner</th>
                  <th className="px-4 py-3 text-left font-medium">Open Deals</th>
                  <th className="px-4 py-3 text-left font-medium">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {contacts.data.items.map((c) => (
                  <tr key={c.id} className="border-border hover:bg-rc-grey-50 border-b">
                    <td className="px-4 py-3">
                      <Link
                        to={`/contacts/${c.id}`}
                        className="text-foreground hover:text-rc-blue-600 flex items-center gap-2 font-medium"
                      >
                        {c.isFavourite && (
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        )}
                        {c.firstName} {c.lastName}
                      </Link>
                    </td>
                    <td className="text-muted-foreground px-4 py-3">
                      {c.businessName ?? "—"}
                      {c.industry && (
                        <div className="text-muted-foreground/70 text-xs">{c.industry}</div>
                      )}
                    </td>
                    <td className="text-muted-foreground px-4 py-3">
                      {c.email && <div className="max-w-[220px] truncate">{c.email}</div>}
                      {c.mobile && <div className="text-xs">{formatPhone(c.mobile)}</div>}
                      {!c.email && !c.mobile && "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANTS[c.status]}>
                        {STATUS_OPTIONS.find((s) => s.value === c.status)?.label ?? c.status}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground px-4 py-3">
                      {c.ownerName ?? "Unassigned"}
                    </td>
                    <td className="px-4 py-3">
                      {c.openDeals && c.openDeals.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {c.openDeals.map((d) => (
                            <Link
                              key={d.id}
                              to={`/deals/${d.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-1.5 py-0.5 text-[10px] hover:border-rc-blue-400"
                              onClick={(e) => e.stopPropagation()}
                              title={`${d.dealName ?? "Deal"} — ${formatCurrency(d.naf ?? d.loanAmount ?? 0)}`}
                            >
                              <span
                                className={`inline-block h-1.5 w-1.5 rounded-full ${
                                  d.pipelineStage === "approved"
                                    ? "bg-emerald-500"
                                    : d.pipelineStage === "docs_received"
                                      ? "bg-cyan-500"
                                      : d.pipelineStage === "awaiting_docs"
                                        ? "bg-purple-500"
                                        : d.pipelineStage === "settled"
                                          ? "bg-emerald-600"
                                          : "bg-blue-500"
                                }`}
                              />
                              <span className="capitalize">{d.status.replace(/_/g, " ")}</span>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="text-muted-foreground px-4 py-3 text-xs">
                      {timeAgo(c.updatedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/contacts/${c.id}`}
                        className="text-muted-foreground hover:text-foreground inline-flex"
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Pagination */}
        {contacts.data && contacts.data.total > 50 && (
          <div className="flex items-center justify-between px-1 text-sm">
            <span className="text-muted-foreground">
              Showing {((params.page ?? 1) - 1) * 50 + 1}–
              {Math.min((params.page ?? 1) * 50, contacts.data.total)} of {contacts.data.total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={(params.page ?? 1) <= 1}
                onClick={() => updateFilter("page", String((params.page ?? 1) - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!contacts.data.hasMore}
                onClick={() => updateFilter("page", String((params.page ?? 1) + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
